import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatApiError, useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ROLE_LABELS, ACTION_LABELS, ACTION_TYPE_KEYS } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { UserPlus, Trash2, Loader2, ShieldCheck, Pencil, Save, MessageSquareWarning, Gauge } from "lucide-react";
import { BackupManager } from "../components/BackupManager";
import ProspectCategoriesCard from "../components/ProspectCategoriesCard";

const EMPTY = { name: "", email: "", password: "", role: "qualification", commission_pct: "", notify_web_lead: false, phone: "" };
const MARGIN_SEGS = [["residenziale", "Residenziale"], ["ci", "Commerciale"], ["utility", "Industriale"]];

function PartnerMarginiDialog({ u, isAdmin, onSaved }) {
  const initM = () => {
    const pm = u.partner_margini || {};
    const o = {};
    MARGIN_SEGS.forEach(([k]) => { o[k] = pm[k] ?? ""; o[`${k}_min`] = pm[`${k}_min`] ?? ""; });
    return o;
  };
  const [open, setOpen] = useState(false);
  const [m, setM] = useState(initM);
  const [saving, setSaving] = useState(false);
  const has = MARGIN_SEGS.some(([k]) => { const v = (u.partner_margini || {})[k]; return v != null && v !== ""; });
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${u.id}`, { partner_margini: m });
      toast.success("Marginalità aggiornata");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setM(initM()); }}>
      <DialogTrigger asChild>
        <button className="inline-flex items-center gap-1.5 text-sm hover:text-foreground" data-testid={`margini-edit-${u.id}`}>
          <span className="font-medium">{has ? "Personalizzata" : "Standard"}</span>
          {isAdmin && <Pencil className="h-3.5 w-3.5 text-muted-foreground" />}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-popover sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Marginalità · {u.name}</DialogTitle><DialogDescription className="text-xs">Percentuali per tipologia cliente. Lascia vuoto per usare il margine standard aziendale.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-3 items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground"><span></span><span>Consigliato %</span><span>Minimo %</span></div>
          {MARGIN_SEGS.map(([k, label]) => (
            <div key={k} className="grid grid-cols-3 items-center gap-2">
              <Label className="text-xs">{label}</Label>
              <Input type="number" min="0" step="0.5" placeholder="std" value={m[k]} onChange={(e) => setM((s) => ({ ...s, [k]: e.target.value }))} disabled={!isAdmin} data-testid={`margini-${k}-${u.id}`} />
              <Input type="number" min="0" step="0.5" placeholder="std" value={m[`${k}_min`]} onChange={(e) => setM((s) => ({ ...s, [`${k}_min`]: e.target.value }))} disabled={!isAdmin} data-testid={`margini-${k}-min-${u.id}`} />
            </div>
          ))}
        </div>
        {isAdmin && <DialogFooter><Button onClick={save} disabled={saving} className="font-semibold" data-testid={`margini-save-${u.id}`}>{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva marginalità</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
const ROLE_OPTIONS = [
  { value: "qualification", label: "Lead Qualification" },
  { value: "sales", label: "Technical Sales" },
  { value: "team_leader", label: "Team Leader" },
  { value: "partner", label: "Head of Partner" },
  { value: "partner_commerciale", label: "Partner Commerciale" },
  { value: "admin", label: "Amministratore" },
];

export default function Settings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [phoneEditId, setPhoneEditId] = useState(null);
  const [phoneVal, setPhoneVal] = useState("");
  const [roleEditId, setRoleEditId] = useState(null);
  const [roleVal, setRoleVal] = useState("");
  const [disclaimer, setDisclaimer] = useState("");
  const [savingDisc, setSavingDisc] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (isAdmin) api.get("/settings/partner-disclaimer").then((r) => setDisclaimer(r.data.text || "")).catch(() => {}); }, [isAdmin]);
  const [qCfg, setQCfg] = useState(null);
  const [savingQ, setSavingQ] = useState(false);
  useEffect(() => { if (isAdmin) api.get("/settings/quality").then((r) => setQCfg(r.data)).catch(() => {}); }, [isAdmin]);
  const saveQuality = async () => {
    setSavingQ(true);
    try { const { data } = await api.put("/settings/quality", qCfg); setQCfg(data); toast.success("Assistente Qualità aggiornato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingQ(false); }
  };
  const toggleInList = (key, val) => setQCfg((c) => ({ ...c, [key]: c[key].includes(val) ? c[key].filter((x) => x !== val) : [...c[key], val] }));

  const saveCommission = async (id) => {
    try {
      const val = editVal === "" ? 0 : parseFloat(editVal);
      await api.patch(`/users/${id}`, { commission_pct: val });
      toast.success("Provvigione aggiornata");
      setEditId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveRole = async (id) => {
    try {
      await api.patch(`/users/${id}`, { role: roleVal });
      toast.success("Ruolo aggiornato");
      setRoleEditId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const savePhone = async (id) => {
    try {
      await api.patch(`/users/${id}`, { phone: phoneVal.trim() });
      toast.success("Telefono aggiornato");
      setPhoneEditId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleNotify = async (id, val) => {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, notify_web_lead: val } : u)));
    try {
      await api.patch(`/users/${id}`, { notify_web_lead: val });
      toast.success(val ? "Riceverà le email nuovo lead web" : "Notifiche nuovo lead web disattivate");
    } catch (e) {
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, notify_web_lead: !val } : u)));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (id, val) => {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, active: val } : u)));
    try {
      await api.patch(`/users/${id}`, { active: val });
      toast.success(val ? "Utente attivato" : "Utente disattivato: non potrà più accedere");
    } catch (e) {
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, active: !val } : u)));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveDisclaimer = async () => {
    setSavingDisc(true);
    try {
      await api.put("/settings/partner-disclaimer", { text: disclaimer });
      toast.success("Disclaimer salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingDisc(false); }
  };

  const create = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Compila tutti i campi"); return; }
    setSaving(true);
    try {
      const commission = form.commission_pct === "" ? null : parseFloat(form.commission_pct);
      await api.post("/auth/register", { ...form, commission_pct: form.role === "partner_commerciale" ? commission : null });
      toast.success("Utente creato");
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Impostazioni</h1>
          <p className="text-xs text-muted-foreground">Gestione team e utenti</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="open-new-user-btn" className="font-semibold"><UserPlus className="h-4 w-4 mr-1.5" /> Nuovo utente</Button>
            </DialogTrigger>
            <DialogContent className="bg-popover">
              <DialogHeader><DialogTitle className="font-heading">Crea nuovo utente</DialogTitle><DialogDescription className="sr-only">Inserisci i dati del nuovo utente del team</DialogDescription></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Nome</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="user-name-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-email-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Telefono</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="es. +39 333 1234567" data-testid="user-phone-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label><Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="user-password-input" /></div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Ruolo</Label>
                  <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.role === "partner_commerciale" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Provvigione (%)</Label>
                    <Input type="number" min="0" step="0.5" value={form.commission_pct} onChange={(e) => setForm((f) => ({ ...f, commission_pct: e.target.value }))} data-testid="user-commission-input" placeholder="es. 5" />
                  </div>
                )}
                {form.role !== "partner_commerciale" && (
                  <div className="flex items-center justify-between rounded-md border border-border p-3">
                    <div className="pr-3">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email nuovo lead web</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">Riceve un'email quando un lead completa il configuratore pubblico.</p>
                    </div>
                    <Switch checked={form.notify_web_lead} onCheckedChange={(v) => setForm((f) => ({ ...f, notify_web_lead: v }))} data-testid="user-notify-web-lead-switch" />
                  </div>
                )}
              </div>
              <DialogFooter><Button onClick={create} disabled={saving} data-testid="save-user-btn" className="font-semibold">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Crea utente</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <div className="p-4 lg:p-8">
        <Card className="rounded border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Ruolo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Telefono</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Provvigione</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Marginalità</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Email nuovo lead web</TableHead>
                {isAdmin && <TableHead className="text-xs uppercase tracking-wider">Stato</TableHead>}
                {isAdmin && <TableHead className="text-right"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-border" data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium flex items-center gap-2">{u.role === "admin" && <ShieldCheck className="h-4 w-4 text-primary" />}{u.name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{u.email}</TableCell>
                  <TableCell>
                    {isAdmin && roleEditId === u.id ? (
                      <div className="flex items-center gap-1.5">
                        <Select value={roleVal} onValueChange={setRoleVal}>
                          <SelectTrigger className="h-8 w-44" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" className="h-8 px-2" onClick={() => saveRole(u.id)} data-testid={`role-save-${u.id}`}><Save className="h-3.5 w-3.5" /></Button>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        <Badge variant="outline" className="border-border font-normal" data-testid={`role-value-${u.id}`}>{ROLE_LABELS[u.role]}</Badge>
                        {isAdmin && u.id !== user.id && <button onClick={() => { setRoleEditId(u.id); setRoleVal(u.role); }} className="text-muted-foreground hover:text-foreground" data-testid={`role-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {isAdmin && phoneEditId === u.id ? (
                      <div className="flex items-center gap-1.5">
                        <Input value={phoneVal} onChange={(e) => setPhoneVal(e.target.value)} className="h-8 w-40" placeholder="es. +39 333 1234567" data-testid={`phone-input-${u.id}`} />
                        <Button size="sm" className="h-8 px-2" onClick={() => savePhone(u.id)} data-testid={`phone-save-${u.id}`}><Save className="h-3.5 w-3.5" /></Button>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="font-medium" data-testid={`phone-value-${u.id}`}>{u.phone || "—"}</span>
                        {isAdmin && <button onClick={() => { setPhoneEditId(u.id); setPhoneVal(u.phone ?? ""); }} className="text-muted-foreground hover:text-foreground" data-testid={`phone-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {u.role === "partner_commerciale" ? (
                      isAdmin && editId === u.id ? (
                        <div className="flex items-center gap-1.5">
                          <Input type="number" min="0" step="0.5" value={editVal} onChange={(e) => setEditVal(e.target.value)} className="h-8 w-20" data-testid={`commission-input-${u.id}`} />
                          <Button size="sm" className="h-8 px-2" onClick={() => saveCommission(u.id)} data-testid={`commission-save-${u.id}`}><Save className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="font-medium" data-testid={`commission-value-${u.id}`}>{u.commission_pct != null ? `${u.commission_pct}%` : "—"}</span>
                          {isAdmin && <button onClick={() => { setEditId(u.id); setEditVal(u.commission_pct ?? ""); }} className="text-muted-foreground hover:text-foreground" data-testid={`commission-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
                        </span>
                      )
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-sm">
                    {u.role === "partner_commerciale" ? (
                      <PartnerMarginiDialog u={u} isAdmin={isAdmin} onSaved={load} />
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    {u.role === "partner_commerciale" ? (
                      <span className="text-muted-foreground text-sm">—</span>
                    ) : (
                      <Switch checked={!!u.notify_web_lead} onCheckedChange={(v) => toggleNotify(u.id, v)} disabled={!isAdmin} data-testid={`notify-web-lead-${u.id}`} />
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch checked={u.active !== false} onCheckedChange={(v) => toggleActive(u.id, v)} disabled={u.id === user.id} data-testid={`user-active-${u.id}`} />
                        {u.active === false && <Badge variant="outline" className="border-destructive/40 text-destructive text-[11px] font-normal">Disattivato</Badge>}
                      </div>
                    </TableCell>
                  )}
                  {isAdmin && (
                    <TableCell className="text-right">
                      {u.id !== user.id && <Button variant="ghost" size="sm" onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`} className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        {!isAdmin && <p className="text-xs text-muted-foreground mt-3">Solo gli amministratori possono creare o eliminare utenti.</p>}
        {isAdmin && (
          <Card className="rounded border-border p-5 mt-6" data-testid="partner-disclaimer-card">
            <div className="flex items-center gap-2 mb-1"><MessageSquareWarning className="h-4 w-4 text-amber-500" /><h2 className="font-heading font-semibold">Condizioni proposta partner</h2></div>
            <p className="text-xs text-muted-foreground mb-3">Disclaimer mostrato nell'Outlook dei preventivi generati dai partner commerciali: la proposta è soggetta a conferma da parte dell'azienda e, in caso di elementi sottostimati, a variazioni di prezzo.</p>
            <textarea value={disclaimer} onChange={(e) => setDisclaimer(e.target.value)} rows={4} data-testid="partner-disclaimer-textarea" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
            <div className="mt-3"><Button onClick={saveDisclaimer} disabled={savingDisc} data-testid="save-disclaimer-btn" className="font-semibold">{savingDisc && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva disclaimer</Button></div>
          </Card>
        )}
        {isAdmin && qCfg && (
          <Card className="rounded border-border p-5 mt-6" data-testid="quality-config-card">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2"><Gauge className="h-4 w-4" style={{ color: "#FDD003" }} /><h2 className="font-heading font-semibold">Assistente Qualità CRM</h2></div>
              <Switch checked={qCfg.enabled} onCheckedChange={(v) => setQCfg((c) => ({ ...c, enabled: v }))} data-testid="quality-enabled-switch" />
            </div>
            <p className="text-xs text-muted-foreground mb-4">Analizza con l'AI la descrizione delle attività al salvataggio e avvisa se è troppo sintetica. Non corregge il testo.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Soglia minima ({qCfg.soglia_minima}/100)</Label>
                <Input type="number" min="0" max="100" value={qCfg.soglia_minima} onChange={(e) => setQCfg((c) => ({ ...c, soglia_minima: parseInt(e.target.value || "0", 10) }))} data-testid="quality-soglia-input" />
              </div>
            </div>
            <div className="space-y-1.5 mt-4">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Messaggio di avviso</Label>
              <textarea value={qCfg.messaggio} onChange={(e) => setQCfg((c) => ({ ...c, messaggio: e.target.value }))} rows={2} data-testid="quality-messaggio-textarea" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
            <div className="mt-4">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Attività analizzate</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {ACTION_TYPE_KEYS.map((t) => (
                  <button key={t} type="button" onClick={() => toggleInList("applies_to", t)} data-testid={`quality-applies-${t}`} className={`rounded-full border px-3 py-1 text-xs transition-colors ${qCfg.applies_to.includes(t) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{ACTION_LABELS[t] || t}</button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Ruoli che possono "Salva comunque"</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.keys(ROLE_LABELS).filter((r) => r !== "partner_commerciale").map((r) => (
                  <button key={r} type="button" onClick={() => toggleInList("bypass_roles", r)} data-testid={`quality-bypass-${r}`} className={`rounded-full border px-3 py-1 text-xs transition-colors ${qCfg.bypass_roles.includes(r) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{ROLE_LABELS[r]}</button>
                ))}
              </div>
            </div>
            <div className="mt-4"><Button onClick={saveQuality} disabled={savingQ} data-testid="save-quality-btn" className="font-semibold">{savingQ && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva assistente qualità</Button></div>
          </Card>
        )}
        {isAdmin && <ProspectCategoriesCard />}
        {isAdmin && <BackupManager />}
      </div>
    </div>
  );
}
